<script setup lang="ts">
import { Menu, X } from '@lucide/vue'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useWindowScroll, useWindowSize } from '@vueuse/core'
import LogoMark from '@/components/atoms/LogoMark.vue'

const open = ref(false)
const route = useRoute()
const { t } = useI18n()
const { y } = useWindowScroll()
const { height } = useWindowSize()
watch(() => route.fullPath, () => { open.value = false })

const scrolled = computed(() => y.value > 24)
const progress = computed(() => {
  if (typeof document === 'undefined') return 0
  const max = document.documentElement.scrollHeight - height.value
  return max > 0 ? Math.min(1, y.value / max) : 0
})

const left = [{ to: '/games', key: 'nav.games' }, { to: '/raffles', key: 'nav.raffles' }]
const right = [{ to: '/claim-win', key: 'nav.claim' }, { to: '/contact', key: 'nav.contact' }]
</script>

<template>
  <header class="fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-500" :class="scrolled || open ? 'border-white/10 bg-ink/85 shadow-[0_10px_40px_rgba(0,0,0,.45)] backdrop-blur-xl' : 'border-transparent bg-gradient-to-b from-ink/80 to-transparent'">
    <nav class="page-shell grid grid-cols-[1fr_auto_1fr] items-center transition-[height] duration-500" :class="scrolled ? 'h-[64px]' : 'h-[76px]'" :aria-label="t('brand.name')">
      <div class="hidden items-center gap-8 lg:flex">
        <RouterLink v-for="item in left" :key="item.to" :to="item.to" class="nav-link focus-ring text-xs font-bold uppercase tracking-[.16em] text-zinc-300 transition-colors hover:text-white" active-class="!text-white">{{ t(item.key) }}</RouterLink>
      </div>
      <RouterLink to="/" class="focus-ring group relative justify-self-center" :aria-label="t('brand.name')">
        <span class="block transition-transform duration-500 group-hover:rotate-[-6deg] group-hover:scale-110" :class="scrolled ? 'scale-[.86]' : ''"><LogoMark compact /></span>
        <span class="absolute left-1/2 h-[3px] -translate-x-1/2 bg-signal transition-all duration-500 group-hover:w-14" :class="scrolled ? '-bottom-[13px] w-6' : '-bottom-[19px] w-9'" />
      </RouterLink>
      <div class="hidden items-center justify-end gap-8 lg:flex">
        <RouterLink v-for="item in right" :key="item.to" :to="item.to" class="nav-link focus-ring text-xs font-bold uppercase tracking-[.16em] text-zinc-300 transition-colors hover:text-white" active-class="!text-white">{{ t(item.key) }}</RouterLink>
      </div>
      <button class="focus-ring col-start-3 justify-self-end rounded-lg p-2 transition-transform active:scale-90 lg:hidden" :aria-expanded="open" :aria-label="open ? t('nav.close') : t('nav.menu')" @click="open = !open">
        <Transition mode="out-in" enter-active-class="transition duration-200" enter-from-class="rotate-90 opacity-0" leave-active-class="transition duration-150" leave-to-class="-rotate-90 opacity-0">
          <X v-if="open" /> <Menu v-else />
        </Transition>
      </button>
    </nav>
    <div class="absolute inset-x-0 bottom-0 h-px origin-left bg-gradient-to-r from-signal via-hot to-signal" :style="{ transform: `scaleX(${progress})` }" aria-hidden="true" />
    <Transition enter-active-class="transition duration-300 ease-out" enter-from-class="-translate-y-3 opacity-0" leave-active-class="transition duration-150" leave-to-class="-translate-y-3 opacity-0">
      <div v-if="open" class="border-t border-line bg-ink px-5 py-6 lg:hidden">
        <div class="grid gap-2">
          <RouterLink v-for="(item, index) in [...left, ...right]" :key="item.to" :to="item.to" class="fade-in focus-ring flex items-center justify-between rounded-lg px-4 py-4 font-display text-3xl uppercase transition-colors hover:bg-white/5" :style="{ '--d': `${index * 60}ms` }" active-class="text-signal">
            {{ t(item.key) }}<span class="font-body text-xs text-zinc-600">0{{ index + 1 }}</span>
          </RouterLink>
        </div>
      </div>
    </Transition>
  </header>
</template>
